import React, { useState, useEffect, useCallback } from 'react';
import { GoalsData, KeyGoal, PriorityGoal, TrendGoal } from '../types';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { HelpButton } from '../components/common/HelpButton';
import { useCustomer } from '../context/CustomerContext';
import { ConfirmationModal } from '../components/common/ConfirmationModal';
import { customerApi } from '../services/api';

// Modal components
import KeyGoalModal from '../components/goals/KeyGoalModal';
import PriorityModal from '../components/goals/PriorityModal';
import IndustryTrendModal from '../components/goals/IndustryTrendModal';

interface GoalsEditTabProps {
    customerId?: string;
    onShowHelp?: () => void;
}

const GoalsEditTab: React.FC<GoalsEditTabProps> = ({ customerId: propCustomerId, onShowHelp }) => {
    const { customerId: paramCustomerId } = useParams<{ customerId?: string }>();
    const customerIdToUse = propCustomerId || paramCustomerId || 'cust-12345'; // Use prop, URL param, or default
    const { customerData, updateCustomerData } = useCustomer();

    const initialGoalsData = customerData?.goals;
    const [goalsData, setGoalsData] = useState<GoalsData | null>(initialGoalsData || null);
    const [loading, setLoading] = useState<boolean>(!initialGoalsData);

    // Modal states
    const [isKeyGoalModalOpen, setIsKeyGoalModalOpen] = useState<boolean>(false);
    const [isPriorityModalOpen, setIsPriorityModalOpen] = useState<boolean>(false);
    const [isIndustryTrendModalOpen, setIsIndustryTrendModalOpen] = useState<boolean>(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
    const [goalToDelete, setGoalToDelete] = useState<KeyGoal | null>(null);
    const [deleteGoalAssociatedProjects, setDeleteGoalAssociatedProjects] = useState<{ projectNames: string[]; projectIds: string[] } | null>(null);
    const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState<boolean>(false);
    const [goalToUpdate, setGoalToUpdate] = useState<KeyGoal | null>(null);
    const [updateGoalAssociatedProjects, setUpdateGoalAssociatedProjects] = useState<{ projectNames: string[]; projectIds: string[] } | null>(null);

    // Edit states
    const [editingKeyGoal, setEditingKeyGoal] = useState<KeyGoal | null>(null);
    const [editingPriority, setEditingPriority] = useState<PriorityGoal | null>(null);
    const [editingTrend, setEditingTrend] = useState<TrendGoal | null>(null);

    const fetchGoalsData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await customerApi.getAllGoals(customerIdToUse);
            setGoalsData(data);
            toast.success("Goals data loaded successfully");
        } catch (error) {
            console.error("Error fetching goals data:", error);
            toast.error("Failed to load goals data");
        } finally {
            setLoading(false);
        }
    }, [customerIdToUse]);

    // Fetch goals data if not provided as props
    useEffect(() => {
        if (!initialGoalsData) {
            fetchGoalsData();
        }
    }, [customerIdToUse, initialGoalsData, fetchGoalsData]);

    const syncGoalsToContext = useCallback((data: GoalsData) => {
        setGoalsData(data);
        const goalIdMap = new Map<string, KeyGoal>();
        data.key_goals?.forEach((g: KeyGoal) => { if (g.id) goalIdMap.set(g.id, g); });
        const updatedProjects = (customerData?.top_10_projects?.top_projects || []).map(project => {
            if (!project.goals?.length) return project;
            const projectGoals = project.goals
                .map(pg => (pg.id && goalIdMap.has(pg.id) ? goalIdMap.get(pg.id)! : null))
                .filter((g): g is KeyGoal => g !== null);
            return { ...project, goals: projectGoals };
        });
        updateCustomerData({
            goals: data,
            top_10_projects: {
                ...customerData?.top_10_projects,
                top_projects: updatedProjects,
                industry_type: customerData?.top_10_projects?.industry_type || ''
            }
        });
    }, [customerData?.top_10_projects, updateCustomerData]);

    // Handle key goal actions
    const handleAddKeyGoal = () => {
        setEditingKeyGoal(null);
        setIsKeyGoalModalOpen(true);
    };

    const handleEditKeyGoal = (goal: KeyGoal) => {
        setEditingKeyGoal(goal);
        setIsKeyGoalModalOpen(true);
    };

    const handleSaveKeyGoal = async (goal: KeyGoal): Promise<void | { needsConfirmation: true }> => {
        if (!goalsData) return;

        if (!editingKeyGoal?.id) {
            setLoading(true);
            try {
                await customerApi.createGoal(customerIdToUse, 'KEY_GOALS', { goal: goal.goal, definition: goal.definition });
                toast.success("Key goal added successfully!");
                setIsKeyGoalModalOpen(false);
                const data = await customerApi.getAllGoals(customerIdToUse);
                syncGoalsToContext(data);
            } catch (err) {
                console.error("Error saving key goal:", err);
                toast.error("Failed to save key goal.");
            } finally {
                setLoading(false);
            }
            return;
        }

        try {
            const result = await customerApi.getProjectsByGoalId(customerIdToUse, editingKeyGoal.id);
            const projectNames = result.projects?.map(p => p.projectName ?? p.projectId) ?? [];
            const projectIds = result.projects?.map(p => p.projectId) ?? [];
            if (projectNames.length > 0) {
                setGoalToUpdate(goal);
                setUpdateGoalAssociatedProjects({ projectNames, projectIds });
                setIsUpdateConfirmOpen(true);
                return { needsConfirmation: true };
            }
        } catch {
            // Fall back to saving without confirmation on API error
        }

        setLoading(true);
        try {
            await customerApi.updateGoal(customerIdToUse, editingKeyGoal.id, 'KEY_GOALS', { goal: goal.goal, definition: goal.definition });
            toast.success("Key goal updated successfully!");
            setIsKeyGoalModalOpen(false);
            const data = await customerApi.getAllGoals(customerIdToUse);
            syncGoalsToContext(data);
        } catch (err) {
            console.error("Error saving key goal:", err);
            toast.error("Failed to save key goal.");
        } finally {
            setLoading(false);
        }
    };

    const performUpdateKeyGoal = async (goal: KeyGoal) => {
        if (!goalsData || !goal.id) return;
        setLoading(true);
        try {
            await customerApi.updateGoal(customerIdToUse, goal.id, 'KEY_GOALS', { goal: goal.goal, definition: goal.definition });
            toast.success("Key goal updated successfully!");
            setIsKeyGoalModalOpen(false);
            setIsUpdateConfirmOpen(false);
            setGoalToUpdate(null);
            setUpdateGoalAssociatedProjects(null);
            const data = await customerApi.getAllGoals(customerIdToUse);
            syncGoalsToContext(data);
        } catch (err) {
            console.error("Error saving key goal:", err);
            toast.error("Failed to save key goal.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteKeyGoal = async (goal: KeyGoal) => {
        if (!goalsData || !goal.id) return;

        try {
            const result = await customerApi.getProjectsByGoalId(customerIdToUse, goal.id);
            const projectNames = result.projects?.map(p => p.projectName ?? p.projectId) ?? [];
            const projectIds = result.projects?.map(p => p.projectId) ?? [];
            setDeleteGoalAssociatedProjects({ projectNames, projectIds });
        } catch {
            setDeleteGoalAssociatedProjects({ projectNames: [], projectIds: [] });
        }
        setGoalToDelete(goal);
        setIsDeleteConfirmOpen(true);
    };

    const performDeleteKeyGoal = async (goal: KeyGoal, projectIds: string[] = []) => {
        if (!goalsData) return;
        if (!goal.id) {
            toast.error("Cannot delete: this goal has no id. Please refresh and try again.");
            setIsDeleteConfirmOpen(false);
            setGoalToDelete(null);
            setDeleteGoalAssociatedProjects(null);
            return;
        }
        setLoading(true);
        try {
            await customerApi.deleteGoal(customerIdToUse, 'KEY_GOALS', goal.id, projectIds);
            toast.success("Key goal deleted successfully!");
            setIsDeleteConfirmOpen(false);
            setGoalToDelete(null);
            setDeleteGoalAssociatedProjects(null);
            const data = await customerApi.getAllGoals(customerIdToUse);
            syncGoalsToContext(data);
        } catch (err) {
            console.error("Error deleting key goal:", err);
            toast.error("Failed to delete key goal.");
        } finally {
            setLoading(false);
        }
    };

    // Handle priority actions
    const handleAddPriority = () => {
        setEditingPriority(null);
        setIsPriorityModalOpen(true);
    };

    const handleEditPriority = (priority: PriorityGoal) => {
        setEditingPriority(priority);
        setIsPriorityModalOpen(true);
    };

    const handleSavePriority = async (priority: PriorityGoal) => {
        if (!goalsData) return;
        setLoading(true);
        try {
            if (editingPriority?.id) {
                await customerApi.updateGoal(customerIdToUse, editingPriority.id, 'PRIORITY_GOALS', { name: priority.name, pillar_alignment: priority.pillar_alignment, details: priority.details });
                toast.success("Priority updated successfully!");
            } else {
                await customerApi.createGoal(customerIdToUse, 'PRIORITY_GOALS', { name: priority.name, pillar_alignment: priority.pillar_alignment, details: priority.details });
                toast.success("Priority added successfully!");
            }
            setIsPriorityModalOpen(false);
            const data = await customerApi.getAllGoals(customerIdToUse);
            setGoalsData(data);
            updateCustomerData({ goals: data });
        } catch (err) {
            console.error("Error saving priority:", err);
            toast.error("Failed to save priority.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePriority = async (priority: PriorityGoal) => {
        if (!goalsData) return;
        if (!priority.id) {
            toast.error("Cannot delete: this item has no id. Please refresh and try again.");
            return;
        }
        setLoading(true);
        try {
            await customerApi.deleteGoal(customerIdToUse, 'PRIORITY_GOALS', priority.id);
            toast.success("Priority deleted successfully!");
            const data = await customerApi.getAllGoals(customerIdToUse);
            setGoalsData(data);
            updateCustomerData({ goals: data });
        } catch (err) {
            console.error("Error deleting priority:", err);
            toast.error("Failed to delete priority.");
        } finally {
            setLoading(false);
        }
    };

    // Handle industry trend actions
    const handleAddIndustryTrend = () => {
        setEditingTrend(null);
        setIsIndustryTrendModalOpen(true);
    };

    const handleEditIndustryTrend = (trend: TrendGoal) => {
        setEditingTrend(trend);
        setIsIndustryTrendModalOpen(true);
    };

    const handleSaveIndustryTrend = async (trend: TrendGoal) => {
        if (!goalsData) return;
        setLoading(true);
        try {
            if (editingTrend?.id) {
                await customerApi.updateGoal(customerIdToUse, editingTrend.id, 'TREND_GOALS', { IndustryTrend: trend.IndustryTrend, TrendHighlights: trend.TrendHighlights, TrendDetails: trend.TrendDetails });
                toast.success("Industry trend updated successfully!");
            } else {
                await customerApi.createGoal(customerIdToUse, 'TREND_GOALS', { IndustryTrend: trend.IndustryTrend, TrendHighlights: trend.TrendHighlights, TrendDetails: trend.TrendDetails });
                toast.success("Industry trend added successfully!");
            }
            setIsIndustryTrendModalOpen(false);
            const data = await customerApi.getAllGoals(customerIdToUse);
            setGoalsData(data);
            updateCustomerData({ goals: data });
        } catch (err) {
            console.error("Error saving industry trend:", err);
            toast.error("Failed to save industry trend.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteIndustryTrend = async (trend: TrendGoal) => {
        if (!goalsData) return;
        if (!trend.id) {
            toast.error("Cannot delete: this item has no id. Please refresh and try again.");
            return;
        }
        setLoading(true);
        try {
            await customerApi.deleteGoal(customerIdToUse, 'TREND_GOALS', trend.id);
            toast.success("Industry trend deleted successfully!");
            const data = await customerApi.getAllGoals(customerIdToUse);
            setGoalsData(data);
            updateCustomerData({ goals: data });
        } catch (err) {
            console.error("Error deleting industry trend:", err);
            toast.error("Failed to delete industry trend.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
            </div>
        );
    }

    return (
        <div className="mx-auto px-4">
            {/* Header with Help Button */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex gap-3 items-center">
                    <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Goals, Priorities & Industry Trends</h1>
                    {onShowHelp && <HelpButton onClick={onShowHelp} />}
                </div>
            </div>

            {/* Key Goals Section */}
            <div className="mb-10">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-2xl font-bold italic text-[#0F2A4F]">Key Goals</h2>
                    <button
                        onClick={handleAddKeyGoal}
                        className="flex items-center px-3.5 py-2.5 text-sm text-black font-medium bg-gray-100  rounded-md hover:bg-gray-200"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Key Goal
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {goalsData?.key_goals.map((goal, index) => (
                        <div
                            key={index}
                            className="rounded-lg shadow-md overflow-hidden border border-gray-200 bg-transparent flex"
                        >
                            <div className="w-7 bg-[#859DB5]"></div>
                            <div className="p-4 flex justify-between flex-grow">
                                <div>
                                    <h3 className="font-bold text-lg">{goal.goal}</h3>
                                    <p className="text-gray-700 mt-1 text-sm">{goal.definition}</p>
                                </div>
                                <button
                                    onClick={() => handleEditKeyGoal(goal)}
                                    className="text-gray-500 hover:text-gray-700 p-2"
                                >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 2.50019H4.16667C3.72464 2.50019 3.30072 2.67578 2.98816 2.98834C2.67559 3.3009 2.5 3.72483 2.5 4.16686V15.8335C2.5 16.2755 2.67559 16.6995 2.98816 17.012C3.30072 17.3246 3.72464 17.5002 4.16667 17.5002H15.8333C16.2754 17.5002 16.6993 17.3246 17.0118 17.012C17.3244 16.6995 17.5 16.2755 17.5 15.8335V10.0002M15.3125 2.18769C15.644 1.85617 16.0937 1.66992 16.5625 1.66992C17.0313 1.66992 17.481 1.85617 17.8125 2.18769C18.144 2.51921 18.3303 2.96885 18.3303 3.43769C18.3303 3.90653 18.144 4.35617 17.8125 4.68769L10.3017 12.1994C10.1038 12.3971 9.85934 12.5418 9.59083 12.6202L7.19667 13.3202C7.12496 13.3411 7.04895 13.3424 6.97659 13.3238C6.90423 13.3053 6.83819 13.2676 6.78537 13.2148C6.73255 13.162 6.69491 13.096 6.67637 13.0236C6.65783 12.9512 6.65909 12.8752 6.68 12.8035L7.38 10.4094C7.45877 10.1411 7.60378 9.8969 7.80167 9.69936L15.3125 2.18769Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Priorities Section */}
            <div className="mb-10">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-2xl font-bold italic text-[#0F2A4F]">Priorities</h2>
                    <button
                        onClick={handleAddPriority}
                        className="flex items-center px-3.5 py-2.5 text-sm text-black font-medium bg-gray-100  rounded-md hover:bg-gray-200"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Priority
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {goalsData?.priority_goals.map((priority, index) => (
                        <div
                            key={index}
                            className="rounded-lg shadow-md overflow-hidden border border-gray-200 bg-transparent flex"
                        >
                            <div className="w-7 bg-[#0F71C0]"></div>
                            <div className="p-4 flex justify-between flex-grow">
                                <div className="w-full">
                                    <div className="flex gap-3 items-center">
                                        <h3 className="font-bold text-lg">{priority.name}</h3>
                                        <span className="text-gray-800 font-medium text-md pl-3 border-l border-gray-500">{priority.pillar_alignment}</span>
                                    </div>
                                    <div className="mt-2">
                                        <ul className="list-disc pl-5 space-y-1">
                                            {priority.details.map((detail, detailIndex) => (
                                                <li key={detailIndex} className="text-gray-700">{detail}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleEditPriority(priority)}
                                    className="text-gray-500 hover:text-gray-700 ml-4"
                                >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 2.50019H4.16667C3.72464 2.50019 3.30072 2.67578 2.98816 2.98834C2.67559 3.3009 2.5 3.72483 2.5 4.16686V15.8335C2.5 16.2755 2.67559 16.6995 2.98816 17.012C3.30072 17.3246 3.72464 17.5002 4.16667 17.5002H15.8333C16.2754 17.5002 16.6993 17.3246 17.0118 17.012C17.3244 16.6995 17.5 16.2755 17.5 15.8335V10.0002M15.3125 2.18769C15.644 1.85617 16.0937 1.66992 16.5625 1.66992C17.0313 1.66992 17.481 1.85617 17.8125 2.18769C18.144 2.51921 18.3303 2.96885 18.3303 3.43769C18.3303 3.90653 18.144 4.35617 17.8125 4.68769L10.3017 12.1994C10.1038 12.3971 9.85934 12.5418 9.59083 12.6202L7.19667 13.3202C7.12496 13.3411 7.04895 13.3424 6.97659 13.3238C6.90423 13.3053 6.83819 13.2676 6.78537 13.2148C6.73255 13.162 6.69491 13.096 6.67637 13.0236C6.65783 12.9512 6.65909 12.8752 6.68 12.8035L7.38 10.4094C7.45877 10.1411 7.60378 9.8969 7.80167 9.69936L15.3125 2.18769Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Industry Trends Section */}
            <div className="mb-10">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-2xl font-bold italic text-[#0F2A4F]">Industry trends</h2>
                    <button
                        onClick={handleAddIndustryTrend}
                        className="flex items-center px-3.5 py-2.5 text-sm text-black font-medium bg-gray-100  rounded-md hover:bg-gray-200"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Industry Trend
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {goalsData?.trend_goals.map((trend, index) => (
                        <div
                            key={index}
                            className="rounded-lg shadow-md overflow-hidden border border-gray-200 bg-transparent flex"
                        >
                            <div className="w-7 bg-[#FFCD73]"></div>
                            <div className="p-4 flex  flex-grow">
                                <div className="w-full">
                                    <div className="flex gap-3 items-center">
                                        <h3 className="font-bold text-lg ">{trend.IndustryTrend}</h3>
                                        <span className="text-gray-800 font-medium text-md pl-3 border-l border-gray-500">{trend.TrendHighlights}</span>
                                    </div>
                                    <div className="mt-2">
                                        <ul className="list-disc pl-5 space-y-1">
                                            {trend.TrendDetails.map((detail, detailIndex) => (
                                                <li key={detailIndex} className="text-gray-700">{detail}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleEditIndustryTrend(trend)}
                                    className="text-gray-500 hover:text-gray-700 ml-4"
                                >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 2.50019H4.16667C3.72464 2.50019 3.30072 2.67578 2.98816 2.98834C2.67559 3.3009 2.5 3.72483 2.5 4.16686V15.8335C2.5 16.2755 2.67559 16.6995 2.98816 17.012C3.30072 17.3246 3.72464 17.5002 4.16667 17.5002H15.8333C16.2754 17.5002 16.6993 17.3246 17.0118 17.012C17.3244 16.6995 17.5 16.2755 17.5 15.8335V10.0002M15.3125 2.18769C15.644 1.85617 16.0937 1.66992 16.5625 1.66992C17.0313 1.66992 17.481 1.85617 17.8125 2.18769C18.144 2.51921 18.3303 2.96885 18.3303 3.43769C18.3303 3.90653 18.144 4.35617 17.8125 4.68769L10.3017 12.1994C10.1038 12.3971 9.85934 12.5418 9.59083 12.6202L7.19667 13.3202C7.12496 13.3411 7.04895 13.3424 6.97659 13.3238C6.90423 13.3053 6.83819 13.2676 6.78537 13.2148C6.73255 13.162 6.69491 13.096 6.67637 13.0236C6.65783 12.9512 6.65909 12.8752 6.68 12.8035L7.38 10.4094C7.45877 10.1411 7.60378 9.8969 7.80167 9.69936L15.3125 2.18769Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modals */}
            {isKeyGoalModalOpen && (
                <KeyGoalModal
                    isOpen={isKeyGoalModalOpen}
                    onClose={() => setIsKeyGoalModalOpen(false)}
                    onSave={handleSaveKeyGoal}
                    onDelete={handleDeleteKeyGoal}
                    keyGoal={editingKeyGoal}
                />
            )}

            {isPriorityModalOpen && (
                <PriorityModal
                    isOpen={isPriorityModalOpen}
                    onClose={() => setIsPriorityModalOpen(false)}
                    onSave={handleSavePriority}
                    onDelete={handleDeletePriority}
                    priority={editingPriority}
                />
            )}

            {isIndustryTrendModalOpen && (
                <IndustryTrendModal
                    isOpen={isIndustryTrendModalOpen}
                    onClose={() => setIsIndustryTrendModalOpen(false)}
                    onSave={handleSaveIndustryTrend}
                    onDelete={handleDeleteIndustryTrend}
                    trend={editingTrend}
                />
            )}

            {/* Delete Confirmation Modal */}
            {goalToDelete && (
                <ConfirmationModal
                    isOpen={isDeleteConfirmOpen}
                    onClose={() => {
                        setIsDeleteConfirmOpen(false);
                        setGoalToDelete(null);
                        setDeleteGoalAssociatedProjects(null);
                    }}
                    onConfirm={() =>
                        goalToDelete &&
                        performDeleteKeyGoal(goalToDelete, deleteGoalAssociatedProjects?.projectIds ?? [])
                    }
                    title="Delete Goal"
                    message={
                        deleteGoalAssociatedProjects && deleteGoalAssociatedProjects.projectNames.length > 0
                            ? `This goal is associated with ${deleteGoalAssociatedProjects.projectNames.length} project(s): ${deleteGoalAssociatedProjects.projectNames.join(', ')}. Are you sure you want to delete this goal?`
                            : "Are you sure you want to delete this goal?"
                    }
                    confirmButtonText="Delete"
                    cancelButtonText="Cancel"
                />
            )}

            {/* Update Confirmation Modal */}
            {goalToUpdate && (
                <ConfirmationModal
                    isOpen={isUpdateConfirmOpen}
                    onClose={() => {
                        setIsUpdateConfirmOpen(false);
                        setGoalToUpdate(null);
                        setUpdateGoalAssociatedProjects(null);
                    }}
                    onConfirm={() => goalToUpdate && performUpdateKeyGoal(goalToUpdate)}
                    title="Update Goal"
                    message={
                        updateGoalAssociatedProjects && updateGoalAssociatedProjects.projectNames.length > 0
                            ? `This goal is associated with ${updateGoalAssociatedProjects.projectNames.length} project(s): ${updateGoalAssociatedProjects.projectNames.join(', ')}. Updating will update the goal in those projects as well. Do you want to continue?`
                            : "Do you want to continue with this update?"
                    }
                    confirmButtonText="Continue"
                    cancelButtonText="Cancel"
                    variant="primary"
                />
            )}
        </div>
    );
};

export default GoalsEditTab;
